/**
 * Estados transversales: cargando / error / vacío.
 *
 * Regla del portal: **sin datos NO es un error**. Un partido recién creado sin
 * eventos, una temporada sin partidos o una base de equipos nueva muestran un
 * texto que explica qué falta, no un cartel rojo.
 */
import type { ReactNode } from 'react';
import { useT } from '../i18n/use-t';

/** `what` ya viene traducido por quien llama (o cae al genérico "datos"). */
export function Loading({ what }: { what?: string }): JSX.Element {
  const t = useT();
  return (
    <p className="loading" role="status">
      {t('common.loading', { what: what ?? t('common.loadingData') })}
    </p>
  );
}

export function Empty({ children }: { children: ReactNode }): JSX.Element {
  return <p className="empty">{children}</p>;
}

export function ErrorBox({
  message,
  onRetry,
  action,
}: {
  message: string;
  onRetry?: () => void;
  /** Acción extra (ej. "Ir a Ajustes" cuando falta el token). */
  action?: ReactNode;
}): JSX.Element {
  const t = useT();
  return (
    <div className="error-box" role="alert">
      <span>{message}</span>
      <span className="spacer" />
      {action}
      {onRetry ? (
        <button type="button" className="small" onClick={onRetry}>
          {t('common.retry')}
        </button>
      ) : null}
    </div>
  );
}

/**
 * Aviso INFORMATIVO, hermano de `ErrorBox` pero sin el cartel rojo.
 *
 * Es para lo que el usuario no puede ver pero tampoco puede arreglar: un 403
 * (su rol no llega) no es un error de la app ni algo que se resuelva
 * reintentando, así que se explica en tono neutro, con la pista de qué sí puede
 * hacer y, si viene, una acción al lado ("Ver el resumen").
 */
export function Notice({
  message,
  hint,
  action,
}: {
  message: string;
  /** Segunda línea, chica: el detalle de por qué y qué queda disponible. */
  hint?: string;
  action?: ReactNode;
}): JSX.Element {
  return (
    <section className="card" role="status">
      <div className="row">
        <span>{message}</span>
        <span className="spacer" />
        {action}
      </div>
      {hint ? (
        <p className="hint" style={{ marginBottom: 0, marginTop: 8 }}>
          {hint}
        </p>
      ) : null}
    </section>
  );
}

/** Sección con título; el bloque visual básico de todas las páginas. */
export function Card({
  title,
  actions,
  children,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="card">
      {title !== undefined || actions !== undefined ? (
        <div className="row" style={{ marginBottom: 10 }}>
          {title !== undefined ? <h2 style={{ margin: 0 }}>{title}</h2> : null}
          <span className="spacer" />
          {actions}
        </div>
      ) : null}
      {children}
    </section>
  );
}
